import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from '@mantine/form'
import { Box, Button, Checkbox, Group, Stack, Text, TextInput } from '@mantine/core'
import { IconId, IconPhone, IconShieldCheck, IconUser } from '@tabler/icons-react'
import { Dialog } from 'antd-mobile'
import useVerificationCodeTimer from '../../hooks/useVerificationCodeTimer'
import useFaceIdGrayControl from '../hooks/useFaceIdGrayControl'
import useOrderCreation from '../hooks/useOrderCreation'
import { usePaymentChannels } from '../../hooks/usePaymentChannels'
import { TemplateMerchantInterface } from '../../typings/orderTypes'
import TermsOfServiceLabel from './TermsOfServiceLabel'

export default function QueryReportForm({ template }: { template: TemplateMerchantInterface | undefined }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const linkId = searchParams.get('linkId') || ''
  const { shouldUseFaceId } = useFaceIdGrayControl()
  const { label, startTimer, isActive } = useVerificationCodeTimer()
  const { getRoutersString, getFirstAppId } = usePaymentChannels({ linkId: linkId })

  const form = useForm({
    initialValues: {
      name: '',
      phone: '',
      idCard: '',
      smsCode: '',
      termsOfService: false,
    },
    validate: {
      phone: (value) => (value === '' ? '请输入手机号' : !/^1[3-9]\d{9}$/.test(value) ? '请输入正确的手机号' : null),
      smsCode: (value) =>
        shouldUseFaceId ? null : value === '' ? '请输入验证码' : value.length !== 6 ? '请输入6位的验证码' : null,
      idCard: (value) =>
        value === ''
          ? '请输入身份证号'
          : !/(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)
            ? '请输入正确的身份证号'
            : null,
      name: (value) => (value === '' ? '请输入姓名' : null),
    },
  })

  const { loading, createOrder } = useOrderCreation({
    linkId,
    shouldUseFaceId,
    isEnvWeixin: /MicroMessenger/i.test(window.navigator.userAgent),
    template,
    getRoutersString,
    getFirstAppId,
  })

  const handleSubmit = (values: typeof form.values) => {
    if (!values.termsOfService) {
      Dialog.confirm({
        content: <TermsOfServiceLabel />,
        confirmText: '同意并查询',
        cancelText: '不同意',
        onConfirm: () => {
          form.setFieldValue('termsOfService', true)
          createOrder(values)
        },
      }).then()
    } else {
      createOrder(values)
    }
  }

  const handleSendCode = () => {
    const phone = form.values.phone
    const result = form.validateField('phone')
    if (result.hasError) {
      return
    }
    startTimer({ phone: phone, type: 6 })
  }

  return (
    <Box>
      <div className={'flex justify-between'}>
        <div className={'text-left text-lg font-bold'}>{template?.name}</div>
        <div
          className={'text-orange-500 text-sm'}
          onClick={() => {
            navigate('/reportSamples/' + template?.templateId)
          }}
        >
          示例报告
        </div>
      </div>
      <div className={'text-gray-500 text-sm mb-4'}>为保证数据准确，请输入真实信息</div>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput placeholder="请输入本人姓名" leftSection={<IconUser size={16} />} {...form.getInputProps('name')} />
        <TextInput
          mt="md"
          placeholder="请输入本人身份证号"
          leftSection={<IconId size={16} />}
          {...form.getInputProps('idCard')}
        />
        <TextInput
          mt="md"
          placeholder="请输入本人手机号"
          leftSection={<IconPhone size={16} />}
          {...form.getInputProps('phone')}
        />
        {!shouldUseFaceId && (
          <TextInput
            mt="md"
            placeholder="请输入验证码"
            maxLength={6}
            leftSection={<IconShieldCheck size={16} />}
            rightSectionWidth={94}
            rightSection={
              <Button size="xs" variant="subtle" onClick={handleSendCode} disabled={isActive}>
                {label}
              </Button>
            }
            {...form.getInputProps('smsCode')}
          />
        )}
        <Checkbox
          mt="md"
          color={'#3A55DD'}
          label={<TermsOfServiceLabel />}
          {...form.getInputProps('termsOfService', { type: 'checkbox' })}
        />

        <Group justify="space-between" mt="md">
          <Button
            loading={loading}
            type="submit"
            fullWidth
            radius="md"
            size={'md'}
            styles={{
              root: {
                backgroundColor: '#3A55DD',
              },
            }}
          >
            <Text>{`¥${(template?.price ?? 0).toFixed(2)} 立即查询`}</Text>
          </Button>
        </Group>

        <Group className="bg-[#FFF2F2] rounded-lg p-3 mt-4" align="flex-start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="text-sm text-[#FF4D4F] leading-5 block">
              本报告非央行征信报告，我们不提供任何形式的放贷、下款、催收等金融服务，仅在合法授权前提下，为机构客户提供风险评估与决策辅助数据服务。
            </span>
          </div>
        </Group>

        <Stack className={'mt-4 text-sm text-gray-500'} gap={4}>
          <span>1. 已付费查询用户可点击右侧历史报告查看报告。 </span>
          <span>2. 付费之后报告在十分钟内未生成或者对报告数据存在任何疑问，可点击右下角按钮联系客服咨询。 </span>
        </Stack>
      </form>
    </Box>
  )
}
